import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * Generates and downloads a PDF scorecard report
 * @param {Object} report - The report data from the database
 */
export const generateScorecardReport = async (report) => {
  try {
    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Set document properties
    doc.setProperties({
      title: `Scorecard Report - ${report.title}`,
      subject: 'Business Resilience Navigator Scorecard',
      author: 'Business Resilience Navigator',
      creator: 'Business Resilience Navigator'
    });

    // Define colors
    const primaryColor = '#243444';
    const secondaryColor = '#D2BA7B';
    const tertiaryColor = '#4C5454';
    const backgroundColor = '#FFFAFA';

    // Add header
    doc.setFillColor(primaryColor);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(backgroundColor);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Scorecard Report', 105, 15, { align: 'center' });

    // Add report info
    doc.setTextColor(primaryColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Report: ${report.title}`, 20, 40);
    doc.text(`Type: ${report.type}`, 20, 48);
    doc.text(`Generated: ${new Date(report.createdAt).toLocaleDateString()}`, 20, 56);

    let yPos = 70;

    // Add content section
    yPos = addSectionHeader(doc, 'Scorecard Results', yPos);
    
    // Try to parse report content as JSON
    try {
      const content = typeof report.content === 'string' ? JSON.parse(report.content) : report.content;
      
      // If content has scores, display them in a table
      if (content && content.scores) {
        // Add a table with the scores
        doc.autoTable({
          startY: yPos,
          head: [['Category', 'Score', 'Max Score', 'Percentage']], 
          body: Object.entries(content.scores).map(([category, data]) => {
            const score = data.score || 0;
            const maxScore = data.maxScore || 100;
            const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
            return [category, score, maxScore, `${percentage}%`];
          }),
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: backgroundColor,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: '#f5f5f5'
          },
          styles: {
            fontSize: 10,
            cellPadding: 5
          },
          columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 30, halign: 'center' },
            2: { cellWidth: 30, halign: 'center' },
            3: { cellWidth: 30, halign: 'center' }
          }
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
      }
      
      // If content has an overall score, display it prominently
      if (content && (content.overallScore !== undefined || content.totalScore !== undefined)) {
        const overallScore = content.overallScore || content.totalScore || 0;
        const normalizedScore = overallScore < 1 ? Math.round(overallScore * 100) : Math.round(overallScore);
        
        // Add a circle with the overall score
        doc.setFillColor(secondaryColor);
        doc.circle(170, 50, 15, 'F');
        doc.setTextColor(backgroundColor);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`${normalizedScore}%`, 170, 50, { align: 'center' });
        doc.setTextColor(primaryColor);
        doc.setFontSize(10);
        doc.text('Overall Score', 170, 70, { align: 'center' });
      }
      
      // If content has recommendations, display them
      if (content && content.recommendations && content.recommendations.length > 0) {
        // Check if we need a new page
        if (yPos > 220) {
          doc.addPage();
          yPos = 20;
        }
        
        yPos = addSectionHeader(doc, 'Recommendations', yPos);
        yPos = addBulletList(doc, content.recommendations, yPos);
        yPos += 10;
      }
      
      // If content has areas of improvement, display them
      if (content && content.areasOfImprovement && content.areasOfImprovement.length > 0) {
        // Check if we need a new page
        if (yPos > 220) {
          doc.addPage();
          yPos = 20;
        }
        
        yPos = addSectionHeader(doc, 'Areas for Improvement', yPos);
        yPos = addBulletList(doc, content.areasOfImprovement, yPos);
        yPos += 10;
      }
      
      // If content has strengths, display them
      if (content && content.strengths && content.strengths.length > 0) {
        // Check if we need a new page
        if (yPos > 220) {
          doc.addPage();
          yPos = 20;
        }
        
        yPos = addSectionHeader(doc, 'Strengths', yPos);
        yPos = addBulletList(doc, content.strengths, yPos);
        yPos += 10;
      }
    } catch (e) {
      // If content is not JSON or parsing fails, display as plain text
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(tertiaryColor);
      
      if (report.content) {
        const lines = doc.splitTextToSize(report.content, 170);
        doc.text(lines, 20, yPos);
        yPos += lines.length * 5 + 10;
      } else {
        doc.text('No content available for this report.', 20, yPos);
        yPos += 10;
      }
    }

    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(tertiaryColor);
      doc.text(
        `Business Resilience Navigator - Generated on ${new Date().toLocaleDateString()}`,
        105,
        285,
        { align: 'center' }
      );
      doc.text(`Page ${i} of ${pageCount}`, 190, 285);
    }

    // Save the PDF
    const filename = `${report.title.replace(/\s+/g, '_').toLowerCase()}_scorecard_${Date.now()}.pdf`;
    doc.save(filename);
    
    return true;
  } catch (error) {
    console.error('Error generating scorecard PDF report:', error);
    return false;
  }
};

/**
 * Generates and downloads a PDF report from the Industry Analysis page
 * @param {Object} data - The data needed for the report
 * @param {Object} data.organization - The organization data
 * @param {Object} data.assessment - The assessment data
 * @param {Object} data.industryData - Industry data
 * @param {Object} data.industryComparisons - Comparison data
 * @param {Object} data.aiAnalysis - AI-generated analysis
 * @param {Object} data.detailedInsights - Detailed insights
 * @param {Array} data.implementationSteps - Implementation steps with details
 * @param {Array} data.itemsToConsider - Items to consider with details
 */
export const generateIndustryAnalysisReport = async (data) => {
  try {
    const {
      organization,
      assessment,
      industryData,
      industryComparisons,
      aiAnalysis,
      detailedInsights,
      implementationSteps,
      itemsToConsider
    } = data;

    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Set document properties
    doc.setProperties({
      title: `Industry Analysis Report - ${organization?.name || 'Organization'}`,
      subject: 'Business Resilience Navigator Industry Analysis',
      author: 'Business Resilience Navigator',
      creator: 'Business Resilience Navigator'
    });

    // Define colors
    const primaryColor = '#243444';
    const secondaryColor = '#D2BA7B';
    const tertiaryColor = '#4C5454';

    // Add header
    doc.setFillColor(primaryColor);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor('#FFFAFA');
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Industry Analysis Report', 105, 15, { align: 'center' });

    // Add organization and assessment info
    doc.setTextColor(primaryColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Organization: ${organization?.name || 'N/A'}`, 20, 40);
    doc.text(`Industry: ${organization?.industryType || assessment?.industryType || 'N/A'}`, 20, 48);
    doc.text(`Assessment: ${assessment?.name || 'N/A'}`, 20, 56);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 64);

    // Add maturity score
    const score = assessment?.score || 0;
    const normalizedScore = score < 1 ? Math.round(score * 100) : Math.round(score);
    doc.setFillColor(secondaryColor);
    doc.circle(170, 50, 15, 'F');
    doc.setTextColor('#FFFAFA');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${normalizedScore}%`, 170, 50, { align: 'center' });
    doc.setTextColor(primaryColor);
    doc.setFontSize(10);
    doc.text('Maturity Score', 170, 70, { align: 'center' });

    let yPos = 80;

    // Add detailed analysis section
    yPos = addSectionHeader(doc, 'Detailed Analysis', yPos);
    
    const detailedAnalysis = aiAnalysis?.detailedAnalysis || 
      (detailedInsights ? detailedInsights.analysis : 'No detailed analysis available.');
    
    yPos = addText(doc, detailedAnalysis, yPos);
    yPos += 10;

    // Add industry trends section
    yPos = addSectionHeader(doc, 'Industry Trends', yPos);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor);
    doc.text('Key Trends', 20, yPos);
    yPos += 8;
    
    const trends = aiAnalysis?.industryTrends || 
      (industryData ? industryData.trends : []);
    
    yPos = addBulletList(doc, trends, yPos);
    yPos += 5;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor);
    doc.text('Key Industry Risks', 20, yPos);
    yPos += 8;
    
    const risks = industryData ? industryData.keyRisks : [];
    
    yPos = addBulletList(doc, risks, yPos);
    yPos += 10;

    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Add industry comparison section
    yPos = addSectionHeader(doc, 'Industry Comparison', yPos);
    
    if (industryComparisons) {
      const comparisonText = aiAnalysis?.industryComparison || 
        `Compared to industry peers, the organization is ${industryComparisons.overallComparison.organizationScore < industryComparisons.overallComparison.industryAverage ? 'behind' : 'ahead'} in resilience capabilities.`;
      
      yPos = addText(doc, comparisonText, yPos);
      yPos += 5;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Your Score: ${industryComparisons.overallComparison.organizationScore}%`, 20, yPos);
      doc.text(`Industry Average: ${industryComparisons.overallComparison.industryAverage}%`, 100, yPos);
      yPos += 8;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Percentile: ${industryComparisons.overallComparison.percentile}th`, 20, yPos);
      doc.text(`Maturity Level: ${industryComparisons.overallComparison.maturityLevel.name}`, 100, yPos);
      yPos += 15;
      
      // Category comparisons
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Category Comparisons', 20, yPos);
      yPos += 8;
      
      // Create a table for category comparisons
      const categoryHeaders = [
        { header: 'Category', dataKey: 'category' },
        { header: 'Your Score', dataKey: 'score' },
        { header: 'Industry Avg', dataKey: 'industryAverage' },
        { header: 'Difference', dataKey: 'difference' },
        { header: 'Status', dataKey: 'status' }
      ];
      
      const categoryData = industryComparisons.categoryComparisons.map(category => ({
        category: category.category,
        score: `${category.score}%`,
        industryAverage: `${category.industryAverage}%`,
        difference: `${Math.abs(category.difference)}%`,
        status: category.isStrength ? 'Strength' : 'Opportunity'
      }));
      
      doc.autoTable({
        head: [categoryHeaders.map(h => h.header)],
        body: categoryData.map(d => categoryHeaders.map(h => d[h.dataKey])),
        startY: yPos,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [36, 52, 68], // primaryColor
          textColor: [255, 250, 250], // FFFAFA
          fontStyle: 'bold'
        },
        columnStyles: {
          4: {
            fontStyle: 'bold',
            textColor: (data, row) => {
              return data === 'Strength' ? [76, 84, 84] : [124, 18, 39]; // tertiaryColor or red
            }
          }
        }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Add improvement opportunities section
    yPos = addSectionHeader(doc, 'Improvement Opportunities', yPos);
    
    const improvementAreas = aiAnalysis?.improvementOpportunities || 
      (detailedInsights ? detailedInsights.improvementAreas : []);
    
    yPos = addBulletList(doc, improvementAreas, yPos);
    yPos += 10;

    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Add implementation roadmap section
    yPos = addSectionHeader(doc, 'Implementation Roadmap', yPos);
    
    const roadmapSteps = aiAnalysis?.implementationRoadmap || 
      (detailedInsights ? detailedInsights.implementationSteps : []);
    
    // Create a table for implementation steps
    const stepHeaders = [
      { header: '#', dataKey: 'number' },
      { header: 'Step', dataKey: 'step' },
      { header: 'Timeframe', dataKey: 'timeframe' },
      { header: 'Resources', dataKey: 'resources' }
    ];
    
    const stepData = roadmapSteps.map((step, index) => {
      // Handle both array of strings from AI and array of objects from fallback
      const stepObj = typeof step === 'string' 
        ? { step, timeframe: 'Varies', resources: 'Varies' } 
        : step;
      
      return {
        number: index + 1,
        step: stepObj.step,
        timeframe: stepObj.timeframe,
        resources: stepObj.resources
      };
    });
    
    doc.autoTable({
      head: [stepHeaders.map(h => h.header)],
      body: stepData.map(d => stepHeaders.map(h => d[h.dataKey])),
      startY: yPos,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [36, 52, 68], // primaryColor
        textColor: [255, 250, 250], // FFFAFA
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 30 },
        3: { cellWidth: 40 }
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;

    // Add implementation step details
    if (implementationSteps && implementationSteps.length > 0) {
      // Check if we need a new page
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor);
      doc.text('Implementation Step Details', 20, yPos);
      yPos += 10;
      
      for (const stepDetail of implementationSteps) {
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor);
        doc.text(stepDetail.title, 20, yPos);
        yPos += 6;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(tertiaryColor);
        doc.text(`Timeframe: ${stepDetail.timeframe}`, 20, yPos);
        doc.text(`Resources: ${stepDetail.resources}`, 100, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(primaryColor);
        const descriptionLines = doc.splitTextToSize(stepDetail.description, 170);
        doc.text(descriptionLines, 20, yPos);
        yPos += descriptionLines.length * 5 + 5;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Implementation Steps:', 20, yPos);
        yPos += 6;
        
        yPos = addBulletList(doc, stepDetail.implementation, yPos);
        yPos += 5;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Potential Challenges:', 20, yPos);
        yPos += 6;
        
        yPos = addBulletList(doc, stepDetail.potentialChallenges, yPos);
        yPos += 5;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Key Benefits:', 20, yPos);
        yPos += 6;
        
        yPos = addBulletList(doc, stepDetail.keyBenefits, yPos);
        yPos += 10;
      }
    }

    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Add items to consider section
    yPos = addSectionHeader(doc, 'Items to Consider', yPos);
    
    const items = aiAnalysis?.itemsToConsider || 
      (detailedInsights ? detailedInsights.itemsToConsider : []);
    
    yPos = addBulletList(doc, items, yPos);
    yPos += 10;

    // Add item details if available
    if (itemsToConsider && itemsToConsider.length > 0) {
      // Check if we need a new page
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor);
      doc.text('Item Details', 20, yPos);
      yPos += 10;
      
      for (const itemDetail of itemsToConsider) {
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor);
        doc.text(itemDetail.title, 20, yPos);
        yPos += 6;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(primaryColor);
        const descriptionLines = doc.splitTextToSize(itemDetail.description, 170);
        doc.text(descriptionLines, 20, yPos);
        yPos += descriptionLines.length * 5 + 5;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Implementation:', 20, yPos);
        yPos += 6;
        
        yPos = addBulletList(doc, itemDetail.implementation, yPos);
        yPos += 5;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Benefits:', 20, yPos);
        yPos += 6;
        
        yPos = addBulletList(doc, itemDetail.benefits, yPos);
        yPos += 5;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Examples:', 20, yPos);
        yPos += 6;
        
        yPos = addBulletList(doc, itemDetail.examples, yPos);
        yPos += 10;
      }
    }

    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(tertiaryColor);
      doc.text(
        `Business Resilience Navigator - Generated on ${new Date().toLocaleDateString()}`,
        105,
        285,
        { align: 'center' }
      );
      doc.text(`Page ${i} of ${pageCount}`, 190, 285);
    }

    // Save the PDF
    doc.save(`${organization?.name || 'Organization'}_Industry_Analysis_Report.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF report:', error);
    return false;
  }
};

// Helper function to add a section header
const addSectionHeader = (doc, title, yPos) => {
  doc.setFillColor('#243444');
  doc.rect(10, yPos, 190, 8, 'F');
  doc.setTextColor('#FFFAFA');
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, yPos + 5.5);
  return yPos + 12;
};

// Helper function to add text with word wrapping
const addText = (doc, text, yPos) => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#4C5454');
  
  const lines = doc.splitTextToSize(text, 170);
  doc.text(lines, 20, yPos);
  
  return yPos + lines.length * 5;
};

// Helper function to add a bullet list
const addBulletList = (doc, items, yPos) => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#4C5454');
  
  let currentY = yPos;
  
  for (const item of items) {
    // Check if we need a new page
    if (currentY > 270) {
      doc.addPage();
      currentY = 20;
    }
    
    doc.text('•', 20, currentY);
    
    const lines = doc.splitTextToSize(item, 165);
    doc.text(lines, 25, currentY);
    
    currentY += lines.length * 5 + 2;
  }
  
  return currentY;
};
